import { Box, Stack, Paper, List, ListItem, ListItemText, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import EvStationIcon from '@mui/icons-material/EvStation';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useMemo, useState, useRef, useEffect } from 'react';

import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';

// Pastikan path import komponen-komponen di bawah ini sesuai dengan struktur folder Anda
import GanWayPointChart from '../../map-tracking/content/GanWayPointChart';
import GantByTimeChart from '../../map-tracking/content/GantByTimeChart';
import RPMGauge from '../../map-tracking/gauge/RPMGauge';
import SpeedGauge from '../../map-tracking/gauge/SpeedGauge';
import FuelGauge from '../../map-tracking/gauge/FuelGauge';
import EnergyGauge from '../../map-tracking/gauge/EnergyGauge';
import FuelChartBox from '../../map-tracking/content/FuelChartBox';
import { useLanguage } from '../../../utils/i18n';

export default function BottomPopoverTripHistoryForShare({
  open = false,
  onClose,
  isPlaying = false,
  onPlayPause,
  onNext,
  onPrev,
  onSeekToPercent,
  allWaypoints = [],
  currentIndexState = 0,
  onSeekToIndex,
  currentPointSpeed = 0,
  currentRPM = 0,
  currentFuel = { litre: 0, percent: 0, cap: 400 },
  currentProgressColor = '#333',
  gradientCSS = '#ccc',
  playbackSpeed = 1,
  onChangeSpeed,
  tripVehicleTarget
}) {
  const { t } = useLanguage();
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFuelChartOpen, setIsFuelChartOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [chartMode, setChartMode] = useState('waypoint');
  const speedMenuRef = useRef(null);
  const fuelChartRef = useRef(null);
  const moreMenuRef = useRef(null);
  const isEV = tripVehicleTarget?.raw?.raw?.is_ev === true;

  const calculatedTotalDuration = useMemo(() => {
    if (!allWaypoints || allWaypoints.length === 0) return 0;
    return allWaypoints.reduce((total, wp) => {
      const dur = Number(wp.duration) || 0;
      return total + dur;
    }, 0);
  }, [allWaypoints]);

  const pointBasedPercent = useMemo(() => {
    if (!allWaypoints || allWaypoints.length <= 1) return 0;
    return Number(((currentIndexState / (allWaypoints.length - 1)) * 100).toFixed(1));
  }, [allWaypoints, currentIndexState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(event.target)) {
        setIsSpeedMenuOpen(false);
      }
      if (fuelChartRef.current && !fuelChartRef.current.contains(event.target)) {
        setIsFuelChartOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!open) return null;
  const speedOptions = [
    { value: 0.125, label: '-4x' },
    { value: 0.15, label: '-2x' },
    { value: 0.25, label: '-1x' },
    { value: 0.5, label: '1x' },
    { value: 1, label: '2x' },
    { value: 2, label: '4x' },
    { value: 4, label: '8x' }
  ];

  const SpeedSelector = () => (
    <Box sx={{ position: 'relative' }} ref={speedMenuRef}>
      <Box
        onClick={() => setIsSpeedMenuOpen(!isSpeedMenuOpen)}
        sx={{
          height: isMinimized ? 24 : 26,
          minWidth: isMinimized ? 40 : 40,
          px: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid',
          borderColor: isSpeedMenuOpen ? '#485D9A' : '#ccc',
          borderRadius: 1,
          cursor: 'pointer',
          color: '#485D9A',
          fontWeight: 'bold',
          fontSize: isMinimized ? '0.75rem' : '0.85rem',
          bgcolor: '#fff',
          '&:hover': { borderColor: '#485D9A' }
        }}
      >
        <Typography sx={{ fontSize: 12 }}>{speedOptions.find((o) => o.value === playbackSpeed)?.label || `${playbackSpeed}x`}</Typography>
        <ArrowDropDownIcon sx={{ fontSize: isMinimized ? 16 : 18 }} />
      </Box>

      {isSpeedMenuOpen && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            mb: 0.5,
            left: 0,
            bgcolor: '#fff',
            border: '1px solid #ccc',
            borderRadius: 1,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
            zIndex: 9999,
            minWidth: 60,
            fontSize: 12
          }}
        >
          <List disablePadding>
            {speedOptions.map((opt) => (
              <ListItem
                key={opt.value}
                button="true"
                onClick={() => {
                  if (onChangeSpeed) onChangeSpeed(opt.value);
                  setIsSpeedMenuOpen(false);
                }}
                sx={{
                  py: 0.2,
                  px: 1.5,
                  cursor: 'pointer',
                  bgcolor: opt.value === playbackSpeed ? 'rgba(72, 93, 154, 0.1)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(72, 93, 154, 0.05)' }
                }}
              >
                <ListItemText
                  primary={opt.label}
                  primaryTypographyProps={{
                    fontSize: '0.75rem',
                    fontWeight: opt.value === playbackSpeed ? 'bold' : 'normal',
                    color: '#485D9A'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );

  const renderChartModeToggle = () => {
    if (!chartMode || !setChartMode) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#f1f5f9',
          borderRadius: 2,
          p: 0.5,
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Box
          onClick={() => setChartMode('waypoint')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.5,
            py: 0.5,
            borderRadius: 1.5,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            bgcolor: chartMode === 'waypoint' ? '#ffffff' : 'transparent',
            boxShadow: chartMode === 'waypoint' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            color: chartMode === 'waypoint' ? '#1a237e' : '#64748b'
          }}
        >
          <TimelineIcon sx={{ fontSize: 12 }} />
          <Typography sx={{ fontSize: 10, fontWeight: chartMode === 'waypoint' ? 'bold' : 'medium' }}>By Waypoint</Typography>
        </Box>
        <Box
          onClick={() => setChartMode('time')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.5,
            py: 0.5,
            borderRadius: 1.5,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            bgcolor: chartMode === 'time' ? '#ffffff' : 'transparent',
            boxShadow: chartMode === 'time' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            color: chartMode === 'time' ? '#1a237e' : '#64748b'
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 12 }} />
          <Typography sx={{ fontSize: 10, fontWeight: chartMode === 'time' ? 'bold' : 'medium' }}>By Time</Typography>
        </Box>
      </Box>
    );
  };

  const handleExportExcel = async () => {
    try {
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Trip History');

      // Headers
      worksheet.mergeCells('A1:A2');
      worksheet.getCell('A1').value = t('exportWaypoint.licensePlate', 'License Plate');
      worksheet.mergeCells('B1:B2');
      worksheet.getCell('B1').value = t('exportWaypoint.imei', 'IMEI');
      worksheet.mergeCells('C1:C2');
      worksheet.getCell('C1').value = t('exportWaypoint.waypointTime', 'Waypoint Time');
      worksheet.mergeCells('D1:D2');
      worksheet.getCell('D1').value = t('exportWaypoint.dateSend', 'Date Send');
      worksheet.mergeCells('E1:E2');
      worksheet.getCell('E1').value = t('exportWaypoint.coordinate', 'Coordinate');
      worksheet.mergeCells('F1:F2');
      worksheet.getCell('F1').value = t('exportWaypoint.address', 'Address');
      worksheet.mergeCells('G1:G2');
      worksheet.getCell('G1').value = t('exportWaypoint.speed', 'Speed');
      worksheet.mergeCells('H1:H2');
      worksheet.getCell('H1').value = t('exportWaypoint.rpm', 'RPM');
      worksheet.mergeCells('I1:I2');
      worksheet.getCell('I1').value = t('exportWaypoint.fuelConsumed', 'Fuel Consumed (L)');
      worksheet.mergeCells('J1:J2');
      worksheet.getCell('J1').value = t('exportWaypoint.distance', 'Distance');

      worksheet.mergeCells('K1:N1');
      worksheet.getCell('K1').value = t('exportWaypoint.abnormality', 'Abnormality');
      worksheet.getCell('K2').value = t('exportWaypoint.overspeed', 'Overspeed');
      worksheet.getCell('L2').value = t('exportWaypoint.harshAcceleration', 'Harsh Acceleration');
      worksheet.getCell('M2').value = t('exportWaypoint.harshBreaking', 'Harsh Breaking');
      worksheet.getCell('N2').value = t('exportWaypoint.harshCornering', 'Harsh Cornering');

      worksheet.mergeCells('O1:O2');
      worksheet.getCell('O1').value = t('exportWaypoint.angle', 'Angle');
      worksheet.mergeCells('P1:P2');
      worksheet.getCell('P1').value = t('exportWaypoint.satelite', 'Satelite');
      worksheet.mergeCells('Q1:Q2');
      worksheet.getCell('Q1').value = t('exportWaypoint.engine', 'Engine');
      worksheet.mergeCells('R1:R2');
      worksheet.getCell('R1').value = t('exportWaypoint.doorBack', 'Door Back');
      worksheet.mergeCells('S1:S2');
      worksheet.getCell('S1').value = t('exportWaypoint.doorLeft', 'Door Left');
      worksheet.mergeCells('T1:T2');
      worksheet.getCell('T1').value = t('exportWaypoint.doorRight', 'Door Right');
      worksheet.mergeCells('U1:U2');
      worksheet.getCell('U1').value = t('exportWaypoint.panicButton', 'Panic Button');
      worksheet.mergeCells('V1:V2');
      worksheet.getCell('V1').value = t('exportWaypoint.virtualOdo', 'Virtual Odo');
      worksheet.mergeCells('W1:W2');
      worksheet.getCell('W1').value = t('exportWaypoint.powerVoltage', 'Power Voltage');
      worksheet.mergeCells('X1:X2');
      worksheet.getCell('X1').value = t('exportWaypoint.batteryVoltage', 'Battery Voltage');
      worksheet.mergeCells('Y1:Y2');
      worksheet.getCell('Y1').value = t('exportWaypoint.movement', 'Movement');
      worksheet.mergeCells('Z1:Z2');
      worksheet.getCell('Z1').value = t('exportWaypoint.acceleration', 'Acceleration');
      worksheet.mergeCells('AA1:AA2');
      worksheet.getCell('AA1').value = t('exportWaypoint.altitude', 'Altitude');
      worksheet.mergeCells('AB1:AB2');
      worksheet.getCell('AB1').value = t('exportWaypoint.temperature1', 'Temperature-1');
      worksheet.mergeCells('AC1:AC2');
      worksheet.getCell('AC1').value = t('exportWaypoint.temperature2', 'Temperature-2');
      worksheet.mergeCells('AD1:AD2');
      worksheet.getCell('AD1').value = t('exportWaypoint.temperature3', 'Temperature-3');

      // Style Headers
      const cols = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
        'AA',
        'AB',
        'AC',
        'AD'
      ];

      cols.forEach((col) => {
        const c1 = worksheet.getCell(`${col}1`);
        c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF485D9A' } };
        c1.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        c1.alignment = { vertical: 'middle', horizontal: 'center' };
        c1.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

        const c2 = worksheet.getCell(`${col}2`);
        c2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF485D9A' } };
        c2.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        c2.alignment = { vertical: 'middle', horizontal: 'center' };
        c2.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      // Adjust column widths
      worksheet.columns = [
        { width: 15 }, // License Plate
        { width: 20 }, // IMEI
        { width: 25 }, // Waypoint Time
        { width: 25 }, // Date Send
        { width: 25 }, // Coordinate
        { width: 40 }, // Address
        { width: 10 }, // Speed
        { width: 10 }, // RPM
        { width: 15 }, // Fuel Consumed (L)
        { width: 15 }, // Distance
        { width: 15 }, // Overspeed
        { width: 20 }, // Harsh Accel
        { width: 20 }, // Harsh Breaking
        { width: 20 }, // Harsh Cornering
        { width: 10 }, // Angle
        { width: 10 }, // Satelite
        { width: 10 }, // Engine
        { width: 15 }, // Door Back
        { width: 15 }, // Door Left
        { width: 15 }, // Door Right
        { width: 15 }, // Panic Button
        { width: 15 }, // Virtual Odo
        { width: 15 }, // Power Voltage
        { width: 15 }, // Battery Voltage
        { width: 10 }, // Movement
        { width: 15 }, // Acceleration
        { width: 10 }, // Altitude
        { width: 15 }, // Temp 1
        { width: 15 }, // Temp 2
        { width: 15 } // Temp 3
      ];

      let currentRow = 3;

      allWaypoints.forEach((wp) => {
        worksheet.getCell(`A${currentRow}`).value = wp.license_plate || wp.vehicle?.license_plate || '-';
        worksheet.getCell(`B${currentRow}`).value = wp.imei_no || '-';
        worksheet.getCell(`C${currentRow}`).value = wp.start_wp_time || '-';
        worksheet.getCell(`D${currentRow}`).value = wp.date_send || '-';
        worksheet.getCell(`E${currentRow}`).value = `${wp.lat || '-'}, ${wp.lon || '-'}`;
        worksheet.getCell(`F${currentRow}`).value = wp.location || '-';
        worksheet.getCell(`G${currentRow}`).value = wp.speed !== undefined ? wp.speed : '-';
        worksheet.getCell(`H${currentRow}`).value = wp.canbus?.canEngineRPM || '-';
        worksheet.getCell(`I${currentRow}`).value = wp.can_fuel_litre ?? wp.canbus?.canFuelConsumedCounted ?? '-';
        worksheet.getCell(`J${currentRow}`).value = wp.virtual_odo !== undefined ? wp.virtual_odo : '-';

        const harsh = (wp.harsh_event || '').toLowerCase();
        worksheet.getCell(`K${currentRow}`).value = harsh.includes('overspeed') ? 'Yes' : 'No';
        worksheet.getCell(`L${currentRow}`).value = harsh.includes('acceleration') ? 'Yes' : 'No';
        worksheet.getCell(`M${currentRow}`).value = harsh.includes('breaking') ? 'Yes' : 'No';
        worksheet.getCell(`N${currentRow}`).value = harsh.includes('cornering') ? 'Yes' : 'No';

        worksheet.getCell(`O${currentRow}`).value = wp.angle !== undefined ? wp.angle : '-';
        worksheet.getCell(`P${currentRow}`).value = wp.satellites !== undefined ? wp.satellites : '-';
        worksheet.getCell(`Q${currentRow}`).value = wp.engine || '-';
        worksheet.getCell(`R${currentRow}`).value = wp.ain1 ?? '-';
        worksheet.getCell(`S${currentRow}`).value = wp.ain2 ?? '-';
        worksheet.getCell(`T${currentRow}`).value = wp.ain3 ?? '-';
        worksheet.getCell(`U${currentRow}`).value = wp.ain4 ?? '-';
        worksheet.getCell(`V${currentRow}`).value = wp.virtual_odo ?? '-';
        worksheet.getCell(`W${currentRow}`).value = wp.power_volt ?? '-';
        worksheet.getCell(`X${currentRow}`).value = wp.batt_volt ?? '-';
        worksheet.getCell(`Y${currentRow}`).value = wp.movement ?? '-';
        worksheet.getCell(`Z${currentRow}`).value = wp.acceleration ?? '-';
        worksheet.getCell(`AA${currentRow}`).value = wp.altitude ?? '-';
        worksheet.getCell(`AB${currentRow}`).value = wp.temperature1 ?? '-';
        worksheet.getCell(`AC${currentRow}`).value = wp.temperature2 ?? '-';
        worksheet.getCell(`AD${currentRow}`).value = wp.temperature3 ?? '-';

        cols.forEach((col) => {
          const c = worksheet.getCell(`${col}${currentRow}`);
          c.alignment = { vertical: 'middle', horizontal: 'center' };
          c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        currentRow++;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Trip_History.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Failed to export to Excel', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
      <style>{`
        .trip-progress { 
          appearance: none; 
          width: 100%; 
          height: ${isMinimized ? '6px' : '10px'}; 
          border-radius: 10px; 
          outline: none;
          cursor: pointer;
        }
        .trip-progress::-webkit-slider-thumb { 
          appearance: none; 
          width: ${isMinimized ? '14px' : '15px'}; 
          height: ${isMinimized ? '14px' : '15px'}; 
          border-radius: 50%; 
          background: #ffffff; 
          border: ${isMinimized ? '2px' : '3px'} solid ${currentProgressColor};
          cursor: pointer;
          transition: transform 0.1s;
        }
        .trip-progress::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }
        .trip-progress::-moz-range-thumb { 
          border-color: ${currentProgressColor} !important; 
        }
        .trip-btn { 
          background:transparent; 
          border:none; 
          padding: ${isMinimized ? '4px' : '8px'}; 
          border-radius:8px; 
          cursor:pointer; 
          display:flex; 
          align-items:center; 
          justify-content:center; 
          transition: all 0.2s;
        }
        .trip-btn:hover { 
          background: rgba(25,118,210,0.1); 
          transform: scale(1.05); 
        }
      `}</style>

      <Paper
        sx={{
          background: 'rgba(255,255,255,0.95)',
          padding: isMinimized ? 1 : 1.5,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          borderTop: '1px solid #e0e0e0',
          position: 'relative',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)' // Sedikit bayangan atas untuk public share
        }}
      >
        {/* HEADER KANAN ATAS (More Menu, Minimize, Close) */}
        <Box sx={{ position: 'absolute', top: isMinimized ? -30 : 8, right: 8, display: 'flex', zIndex: 10, gap: 0.5 }}>
          {/* MORE MENU */}
          <Box ref={moreMenuRef} sx={{ position: 'relative' }}>
            <Box
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              sx={{
                width: 23,
                height: 23,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                borderRadius: 1.5,
                bgcolor: '#ffffff',
                color: '#485D9A',
                '&:hover': { bgcolor: '#e2e8f0' },
                transition: 'all 0.2s'
              }}
            >
              <Tooltip title="More Actions">
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            </Box>

            {isMoreMenuOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  mb: 1,
                  p: 0.5,
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                  zIndex: 9999,
                  minWidth: 180,
                  animation: 'fadeIn 0.2s ease-out'
                }}
              >
                <List disablePadding>
                  <ListItem
                    button
                    onClick={() => {
                      handleExportExcel();
                      setIsMoreMenuOpen(false);
                    }}
                    sx={{ borderRadius: 1, mb: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, py: 1 }}
                  >
                    <FileDownloadIcon sx={{ fontSize: 18, mr: 1.5, color: '#64748b' }} />
                    <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>Export to Excel</Typography>
                  </ListItem>
                  <ListItem
                    button
                    onClick={() => {
                      setIsFuelChartOpen(true);
                      setIsMoreMenuOpen(false);
                    }}
                    sx={{ borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, py: 1 }}
                  >
                    {isEV ? (
                      <EvStationIcon sx={{ fontSize: 18, mr: 1.5, color: '#64748b' }} />
                    ) : (
                      <LocalGasStationIcon sx={{ fontSize: 18, mr: 1.5, color: '#64748b' }} />
                    )}
                    <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>
                      {isEV ? 'Energy History' : 'Fuel History'}
                    </Typography>
                  </ListItem>
                </List>
              </Box>
            )}

            {/* Container for Popups */}
            <Box ref={fuelChartRef} sx={{ position: 'absolute', right: 0, bottom: '100%' }}>
              <FuelChartBox
                isOpen={isFuelChartOpen}
                onClose={() => setIsFuelChartOpen(false)}
                allWaypoints={allWaypoints}
                licensePlate={tripVehicleTarget?.vehicle_license_plate || allWaypoints[0]?.license_plate || '-'}
                fuelCapacity={tripVehicleTarget?.vehicle?.fuel_capacity || 380}
                isEV={isEV}
              />
            </Box>
          </Box>

          <Box
            onClick={() => setIsMinimized(!isMinimized)}
            sx={{
              width: 23,
              height: 23,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: 1,
              bgcolor: '#485D9A',
              color: '#ffffff',
              border: '1px solid',
              borderColor: isFuelChartOpen ? '#1a237e' : '#1a237e',
              '&:hover': { bgcolor: '#e2e8f0' }
            }}
          >
            {isMinimized ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Box
            onClick={onClose}
            sx={{
              width: 23,
              height: 23,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: 1,
              color: '#ffffff',
              bgcolor: '#f60000',
              '&:hover': { bgcolor: '#d40606', color: '#ffffff' }
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>

        {isMinimized ? (
          <Box>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <input
                className="trip-progress"
                type="range"
                min={0}
                max={100}
                value={pointBasedPercent}
                step={0.1}
                style={{ background: `linear-gradient(to right, #485D9A ${pointBasedPercent}%, #ccc ${pointBasedPercent}%)` }}
                onChange={(e) => {
                  const pct = Number(e.target.value || 0);
                  if (onSeekToPercent) onSeekToPercent(pct);
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 'max-content' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#485D9A' }}>
                  Spd: {currentPointSpeed} km/h
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                  RPM: {currentRPM}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                  {isEV ? 'Energy:' : 'Fuel:'} {Math.round(currentFuel.percent)}%
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={onPrev}
                  title="Previous"
                  sx={{
                    color: '#485D9A',
                    bgcolor: 'white',
                    width: 30,
                    height: 30,
                    '&:hover': { bgcolor: '#EDE7F6', transform: 'scale(1.05)' },
                    transition: 'all 0.2s'
                  }}
                >
                  <SkipPreviousRoundedIcon />
                </IconButton>

                <IconButton
                  className="trip-btn"
                  title="Play/Pause"
                  style={{ background: isPlaying ? 'rgba(72, 93, 154, 0.1)' : 'transparent' }}
                  onClick={onPlayPause}
                >
                  {isPlaying ? (
                    <PauseIcon sx={{ color: '#D9534F', fontSize: 18 }} />
                  ) : (
                    <PlayArrowIcon sx={{ color: '#485D9A', fontSize: 18 }} />
                  )}
                </IconButton>

                <IconButton
                  onClick={onNext}
                  title="Next"
                  sx={{
                    color: '#485D9A',
                    bgcolor: 'white',
                    width: 30,
                    height: 30,
                    '&:hover': { bgcolor: '#EDE7F6', transform: 'scale(1.05)' },
                    transition: 'all 0.2s'
                  }}
                >
                  <SkipNextRoundedIcon />
                </IconButton>
                <SpeedSelector />
              </Stack>

              <Box sx={{ flexGrow: 1, width: '100%' }}>
                {allWaypoints &&
                  allWaypoints.length > 0 &&
                  (chartMode === 'waypoint' ? (
                    <GanWayPointChart
                      waypoints={allWaypoints}
                      currentIndex={currentIndexState}
                      onScrub={onSeekToIndex}
                      totalDur={calculatedTotalDuration}
                      isMinimized={true}
                      chartMode={chartMode}
                      onChartModeChange={setChartMode}
                    />
                  ) : (
                    <GantByTimeChart
                      waypoints={allWaypoints}
                      currentIndex={currentIndexState}
                      onScrub={onSeekToIndex}
                      totalDur={calculatedTotalDuration}
                      isMinimized={true}
                      chartMode={chartMode}
                      onChartModeChange={setChartMode}
                    />
                  ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: 14 }} fontWeight="bold" color="#1e293b">
                  {t('mapsTracking.activityTimeline', 'Activity Timeline')}
                </Typography>
                {renderChartModeToggle()}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* AREA GAUGE (SPEED, RPM, FUEL) */}
                <Stack direction="row" spacing={1}>
                  <SpeedGauge value={currentPointSpeed} />
                  <RPMGauge value={currentRPM} />
                  {isEV ? (
                    <EnergyGauge kwh={currentFuel.litre} percentage={currentFuel.percent} capacity={currentFuel.cap} />
                  ) : (
                    <FuelGauge litre={currentFuel.litre} percentage={currentFuel.percent} capacity={currentFuel.cap} />
                  )}
                </Stack>

                {/* AREA KONTROL PEMUTARAN */}
                <Box sx={{ width: '100%', maxWidth: 180 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 2,
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    <IconButton
                      onClick={onPrev}
                      title="Previous"
                      sx={{
                        color: '#485D9A',
                        bgcolor: 'white',
                        width: 25,
                        height: 25,
                        boxShadow: '0 2px 6px rgba(94, 53, 177, 0.2)',
                        '&:hover': { bgcolor: '#EDE7F6', transform: 'scale(1.05)' },
                        transition: 'all 0.2s'
                      }}
                    >
                      <SkipPreviousRoundedIcon />
                    </IconButton>

                    <IconButton
                      onClick={onPlayPause}
                      title="Play/Pause"
                      sx={{
                        color: 'white',
                        bgcolor: isPlaying ? '#D32F2F' : '#485D9A',
                        width: 25,
                        height: 25,
                        boxShadow: isPlaying ? '0 4px 12px rgba(211, 47, 47, 0.4)' : '0 4px 12px rgba(94, 53, 177, 0.4)',
                        '&:hover': {
                          bgcolor: isPlaying ? '#C62828' : '#485D9A',
                          transform: 'scale(1.08)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      {isPlaying ? <PauseRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
                    </IconButton>

                    <IconButton
                      onClick={onNext}
                      title="Next"
                      sx={{
                        color: '#485D9A',
                        bgcolor: 'white',
                        width: 25,
                        height: 25,
                        boxShadow: '0 2px 6px rgba(94, 53, 177, 0.2)',
                        '&:hover': { bgcolor: '#EDE7F6', transform: 'scale(1.05)' },
                        transition: 'all 0.2s'
                      }}
                    >
                      <SkipNextRoundedIcon />
                    </IconButton>

                    <Box sx={{ borderLeft: '2px solid #D1C4E9', pl: 1.5 }}>
                      <SpeedSelector />
                    </Box>
                  </Box>

                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                    <input
                      className="trip-progress"
                      type="range"
                      min={0}
                      max={100}
                      value={pointBasedPercent}
                      step={0.1}
                      style={{ background: `linear-gradient(to right, #485D9A ${pointBasedPercent}%, #ccc ${pointBasedPercent}%)` }}
                      onChange={(e) => {
                        const pct = Number(e.target.value || 0);
                        if (onSeekToPercent) onSeekToPercent(pct);
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      fontSize: 11,
                      color: '#666',
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 1,
                      mt: 0.5,
                      fontWeight: 'bold'
                    }}
                  >
                    <div>0%</div>
                    <div style={{ color: currentProgressColor }}>{Math.round(pointBasedPercent)}%</div>
                    <div>100%</div>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* AREA GANTT CHART */}
            {allWaypoints && allWaypoints.length > 0 && (
              <Box sx={{ width: '100%' }}>
                {chartMode === 'waypoint' ? (
                  <GanWayPointChart
                    waypoints={allWaypoints}
                    currentIndex={currentIndexState}
                    onScrub={onSeekToIndex}
                    totalDur={calculatedTotalDuration}
                    isMinimized={false}
                    chartMode={chartMode}
                    onChartModeChange={setChartMode}
                  />
                ) : (
                  <GantByTimeChart
                    waypoints={allWaypoints}
                    currentIndex={currentIndexState}
                    onScrub={onSeekToIndex}
                    totalDur={calculatedTotalDuration}
                    isMinimized={false}
                    chartMode={chartMode}
                    onChartModeChange={setChartMode}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
